import { Injectable } from "@angular/core";
import { Key, ModifierKey, ShortcutMetadata } from "@tylertech/tyler-api-types";
import { ShortcutGroupKey } from "./omniBarService";

export type GroupedKeyHandlerId = `${string}|${string}|${string}|${string}|${string}`;

export type KeyHandlerId = GroupedKeyHandlerId;

export class ShortcutEvent
{
    KeyHandlerId: KeyHandlerId;
    MatchKey: Key;
    Modifier: ModifierKey;
    Callback: () => void;
}

@Injectable()
export class KeyHandlerService
{
    private readonly shortcutEvents: ShortcutEvent[] = [];

    private getKeyEnum(stringKey: string): Key
    {
        return Key[Object.keys(Key)
            .find(x => x.toLowerCase() === stringKey?.toLowerCase())];
    }

    private getModifierKeyEnum(event: KeyboardEvent): ModifierKey
    {
        if (event.altKey)
            return ModifierKey.Alt;
        if (event.ctrlKey)
            return ModifierKey.Ctrl;
        if (event.shiftKey)
            return ModifierKey.Shift;
        if (event.metaKey)
            return ModifierKey.Meta;
        return undefined;
    }

    getListenerKeys(): Array<KeyHandlerId>
    {
        return Array.from(this.shortcutEvents.map(x => x.KeyHandlerId));
    }

    private hasModifierKey(event: KeyboardEvent): boolean
    {
        return event.altKey || event.ctrlKey || event.shiftKey || event.metaKey;
    }

    private keyIsModifier(event: KeyboardEvent): boolean
    {
        return event.key === "Alt" || event.key === "Control" || event.key === "Shift" || event.key === "Meta";
    }

    public ignoreForFormatting(event: KeyboardEvent, includeShift: boolean = true): boolean
    {
        const key = this.getKeyEnum(event.key);
        return (includeShift ? event.shiftKey : false)
            || key === Key.Asterisk
            || key === Key.Tab
            || key === Key.Enter
            || key === Key.Home
            || key === Key.End
            || key === Key.Delete
            || key === Key.Backspace
            || key === Key.ArrowUp
            || key === Key.ArrowDown
            || key === Key.ArrowLeft
            || key === Key.ArrowRight
            || event.ctrlKey
            || event.metaKey
            || event.key === "-";
    }

    public selectNextTabbableElement(): void
    {
        const tabbableElements = document.querySelectorAll('input:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), forge-button:not([disabled]):not([tabindex="-1"]), forge-icon-button:not([disabled]):not([tabindex="-1"]):not(.generic-button), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), forge-checkbox:not([disabled]):not([tabindex="-1"]), forge-select:not([disabled]):not([tabindex="-1"]), metadata-link a:not([disabled]):not([tabindex="-1"])');
        const currentFocus = document.activeElement;
        let nextElement = null;

        for (let i = 0; i < tabbableElements.length; i++)
        {
            if (tabbableElements[i] === currentFocus)
            {
                nextElement = tabbableElements[i + 1] || tabbableElements[0];

                if(currentFocus.parentElement === nextElement.parentElement) //Don't set next element to an embedded element within the same parent
                {
                    nextElement = tabbableElements[i + 2];
                }
                break;
            }
        }

        if (nextElement)
        {
            nextElement.focus();
        }
    }

    private isMetadataPanelInput(): boolean
    {
        const el = window.document.activeElement as HTMLElement;
        if (!el)
            return false;
        if (el.closest("metadata-action-dialog"))
            return false; //input is on a dialog, do not use Enter for Apply in grid
        if (el.closest("metadata-panel"))
            return true;
        return false;
    }

    handleKeyDown($event: KeyboardEvent): void
    {
        if ((!this.hasModifierKey($event) && $event.key !== "Enter") || this.keyIsModifier($event))
            return;
        let modifierKey = this.getModifierKeyEnum($event);

        if ($event.key === "Enter")
        {
            if (!this.isMetadataPanelInput() && modifierKey !== ModifierKey.Ctrl)
            {
                return;
            }

            modifierKey = ModifierKey.Ctrl;
        }

        this.doCallback($event.key, modifierKey);
    }

    doCallback(key: string, modifierKey: ModifierKey): void
    {
        const shortcutEvent = this.shortcutEvents.find(x => x.MatchKey === this.getKeyEnum(key) && x.Modifier === modifierKey);
        if (shortcutEvent)
        {
            const el = window.document.activeElement as HTMLInputElement;
            if (el)
                el.blur();
            shortcutEvent?.Callback();
        }
    }

    registerShortcutMetadata(
        handlerId: string,
        shortcutMetadata: ShortcutMetadata,
        groupAndDescription: `${ShortcutGroupKey}|${string}`,
        callback: () => void
    ): void
    {
        if (!shortcutMetadata)
            return;
        this.subscribe(handlerId, shortcutMetadata.Key, shortcutMetadata.ModifierKey, groupAndDescription, callback);
    }

    subscribe(
        handlerId: string,
        matchKey: Key,
        modifier: ModifierKey,
        groupAndDescription: `${ShortcutGroupKey}|${string}`,
        callback: () => void
    ): void
    {
        const handlerKey: KeyHandlerId = `${handlerId}|${matchKey}|${modifier}|${groupAndDescription}`;
        const existing = this.shortcutEvents.find(x => x.MatchKey === matchKey && x.Modifier === modifier);
        if (existing)
        {
            const index = this.shortcutEvents.indexOf(existing);
            if (index > -1)
                this.shortcutEvents.splice(index, 1);
        }

        this.shortcutEvents.push({
            KeyHandlerId: handlerKey,
            MatchKey: matchKey,
            Modifier: modifier,
            Callback: callback}
        );
    }

    unsubscribe(targetKey: Key, targetModifier: ModifierKey = null): void
    {
        const matchedBindings = this.shortcutEvents.filter(x => x.MatchKey === targetKey && x.Modifier === targetModifier);
        this.deregisterShortcuts(matchedBindings);
    }

    unsubscribeAllFromClass(handlerId: string): void
    {
        const matchedBindings = this.shortcutEvents.filter(k => k.KeyHandlerId.startsWith(handlerId));
        this.deregisterShortcuts(matchedBindings);
    }

    private deregisterShortcuts(shortcuts: ShortcutEvent[]): void
    {
        shortcuts.forEach(s =>
        {
            const index = this.shortcutEvents.indexOf(s);
            if (index > -1)
                this.shortcutEvents.splice(index, 1);
        });
    }
}
